import { LOCALE_KEY } from "@/constants/app.constants"
import { nonEmptyStringOf } from "@/utils/unknownShape.utils"
import { isRecord } from "@/utils/typeGuards.utils"

export type VerifiedSession = {
    userId: string
    orgId: string
}

export type SessionMarkerStorage = Pick<
    Storage,
    "getItem" | "setItem" | "removeItem"
>

const LAST_VERIFIED_SESSION_KEY = "wilson:lastVerifiedSession"

export function verifiedSessionOf(value: unknown): VerifiedSession | null {
    if (!isRecord(value)) return null
    const userId = nonEmptyStringOf(value.userId)
    const orgId = nonEmptyStringOf(value.orgId)
    return userId && orgId ? { userId, orgId } : null
}

export function sessionOfMyUser(
    user: { id: string; sessionOrgId: string } | undefined
): VerifiedSession | null {
    if (!user?.id || !user.sessionOrgId) return null
    return { userId: user.id, orgId: user.sessionOrgId }
}

export function isSameSession(
    a: VerifiedSession | null,
    b: VerifiedSession | null
): boolean {
    if (a === null || b === null) return a === b
    return a.userId === b.userId && a.orgId === b.orgId
}

const sessionShownByPage = new WeakMap<Window, VerifiedSession>()

export function isPageShowingAnotherSession(session: VerifiedSession): boolean {
    if (typeof window === "undefined") return false
    const shown = sessionShownByPage.get(window)
    return shown !== undefined && !isSameSession(shown, session)
}

export function claimPageForSession(session: VerifiedSession): void {
    if (typeof window === "undefined") return
    if (!sessionShownByPage.has(window)) sessionShownByPage.set(window, session)
}

function browserStorage(): SessionMarkerStorage | null {
    try {
        return typeof localStorage === "undefined" ? null : localStorage
    } catch {
        // Blocked storage reads as no remembered session, so nothing restores early.
        return null
    }
}

export function rememberedSessionOf(
    raw: string | null
): VerifiedSession | null {
    if (!raw) return null
    try {
        return verifiedSessionOf(JSON.parse(raw))
    } catch {
        // A marker that does not parse reads as no remembered session.
        return null
    }
}

export function readLastVerifiedSession(
    storage: SessionMarkerStorage | null = browserStorage()
): VerifiedSession | null {
    try {
        return rememberedSessionOf(
            storage?.getItem(LAST_VERIFIED_SESSION_KEY) ?? null
        )
    } catch {
        // Unreadable storage reads as no remembered session, so nothing restores early.
        return null
    }
}

export function onRememberedSessionChangeElsewhere(
    listener: (session: VerifiedSession | null) => void
): () => void {
    const handleStorage = (event: StorageEvent) => {
        if (event.key !== LAST_VERIFIED_SESSION_KEY) return
        listener(rememberedSessionOf(event.newValue))
    }
    window.addEventListener("storage", handleStorage)
    return () => window.removeEventListener("storage", handleStorage)
}

export function writeLastVerifiedSession(
    session: VerifiedSession | null,
    storage: SessionMarkerStorage | null = browserStorage()
): void {
    try {
        if (session === null) {
            storage?.removeItem(LAST_VERIFIED_SESSION_KEY)
            return
        }
        storage?.setItem(LAST_VERIFIED_SESSION_KEY, JSON.stringify(session))
    } catch {
        // A refused write only means the next visit waits for the server before restoring.
    }
}

export function reloadIntoSwitchedOrg({
    session,
    landingPath,
}: {
    session: VerifiedSession
    landingPath?: string
}): void {
    writeLastVerifiedSession(session)
    localStorage.removeItem(LOCALE_KEY)
    if (landingPath) window.history.replaceState(null, "", landingPath)
    window.location.reload()
}
